import { Component, OnInit } from '@angular/core';
import { FormBuilder, Validators } from '@angular/forms';
import Swal from 'sweetalert2';
import { AuthService } from '../../../core/auth.service';
import { PayrollService } from './payroll.service';
import { StaffService } from '../staff.service';

@Component({
  selector: 'app-staff-payroll',
  templateUrl: './staff-payroll.component.html',
  styleUrls: ['./staff-payroll.component.scss']
})
export class StaffPayrollComponent implements OnInit {
  configs: any[] = [];
  slips: any[] = [];
  staff: any[] = [];
  loadingConfigs = false;
  loadingSlips = false;
  saving = false;

  configForm = this.fb.group({
    roleKey: ['rider', Validators.required],
    commissionPerDelivery: [200, [Validators.required, Validators.min(0)]],
    effectiveFrom: [new Date().toISOString().substring(0, 10), Validators.required]
  });

  slipForm = this.fb.group({
    staffId: [null, Validators.required],
    from: ['', Validators.required],
    to: ['', Validators.required],
    deductions: [0],
    manualUnits: [null],
    includeActivities: [true]
  });

  filterForm = this.fb.group({
    staffId: [null],
    from: [''],
    to: ['']
  });

  deliveryForm = this.fb.group({
    amountPerDelivery: [0, [Validators.required, Validators.min(0)]]
  });
  pricingHistory: any[] = [];

  constructor(
    private fb: FormBuilder,
    private payroll: PayrollService,
    private staffService: StaffService,
    private auth: AuthService
  ) {}

  ngOnInit(): void {
    if (this.canManageCommissionConfig()) {
      this.loadConfigs();
    }
    if (this.canAccessStaffDirectory()) {
      this.loadStaff();
    }
    if (this.canViewSlips()) {
      this.loadSlips();
    }
    if (this.canManageDeliveryPricing()) {
      this.loadDeliveryPricing();
      this.loadDeliveryPricingHistory();
    }
  }

  loadStaff(): void {
    this.staffService.getAll().subscribe({
      next: (res) => (this.staff = res || []),
      error: () => (this.staff = [])
    });
  }

  loadConfigs(): void {
    this.loadingConfigs = true;
    this.payroll.getConfigs().subscribe({
      next: (res) => {
        this.configs = res || [];
        this.loadingConfigs = false;
      },
      error: () => {
        this.loadingConfigs = false;
        Swal.fire('Error', 'Failed to load commission configs.', 'error');
      }
    });
  }

  saveConfig(): void {
    if (this.configForm.invalid) return;
    this.saving = true;
    const payload = {
      roleKey: this.configForm.value.roleKey,
      commissionPerDelivery: Number(this.configForm.value.commissionPerDelivery),
      effectiveFrom: this.configForm.value.effectiveFrom
    };
    this.payroll.saveConfig(payload).subscribe({
      next: (cfg) => {
        Swal.fire('Saved', 'Commission saved.', 'success');
        this.saving = false;
        const existingIdx = this.configs.findIndex((c) => c.roleKey === cfg.roleKey);
        if (existingIdx >= 0) this.configs[existingIdx] = cfg;
        else this.configs.unshift(cfg);
      },
      error: (err) => {
        this.saving = false;
        Swal.fire('Error', err?.error?.message || 'Failed to save config.', 'error');
      }
    });
  }

  loadSlips(): void {
    this.loadingSlips = true;
    const filters = this.filterForm.value;
    this.payroll.getSlips({
      staffId: filters.staffId || undefined,
      from: filters.from || undefined,
      to: filters.to || undefined
    }).subscribe({
      next: (res) => {
        this.slips = res || [];
        this.loadingSlips = false;
      },
      error: () => {
        this.loadingSlips = false;
        Swal.fire('Error', 'Failed to load slips.', 'error');
      }
    });
  }

  loadDeliveryPricing(): void {
    this.payroll.getDeliveryPricing().subscribe({
      next: (cfg) => {
        this.deliveryForm.patchValue({
          amountPerDelivery: cfg?.amountPerDelivery ?? 0
        });
      }
    });
  }

  loadDeliveryPricingHistory(): void {
    this.payroll.getDeliveryPricingHistory().subscribe({
      next: (rows) => (this.pricingHistory = rows || []),
      error: () => (this.pricingHistory = [])
    });
  }

  saveDeliveryPricing(): void {
    if (this.deliveryForm.invalid) return;
    this.payroll.saveDeliveryPricing({
      amountPerDelivery: Number(this.deliveryForm.value.amountPerDelivery || 0)
    }).subscribe({
      next: (cfg) => {
        Swal.fire('Saved', 'Delivery pricing updated.', 'success');
        this.loadDeliveryPricing();
        this.loadDeliveryPricingHistory();
      },
      error: (err) => Swal.fire('Error', err?.error?.message || 'Failed to save pricing.', 'error')
    });
  }

  generateSlip(): void {
    if (this.slipForm.invalid) return;
    const payload = {
      staffId: Number(this.slipForm.value.staffId),
      from: this.slipForm.value.from,
      to: this.slipForm.value.to,
      deductions: Number(this.slipForm.value.deductions || 0),
      manualUnits: this.slipForm.value.manualUnits !== null ? Number(this.slipForm.value.manualUnits) : null,
      includeActivities: this.slipForm.value.includeActivities ?? true
    };
    this.saving = true;
    this.payroll.generateSlip(payload).subscribe({
      next: (slip) => {
        Swal.fire('Generated', 'Payroll slip generated.', 'success');
        this.saving = false;
        this.slips.unshift(slip);
      },
      error: (err) => {
        this.saving = false;
        Swal.fire('Error', err?.error?.message || 'Failed to generate slip.', 'error');
      }
    });
  }

  rateLabel(key: string): string {
    if (!key) return '';
    switch (key) {
      case 'rider':
        return 'Rider';
      case 'logistics_manager':
        return 'Logistics Manager';
      case 'tech':
        return 'Tech';
      default:
        return key;
    }
  }

  canManageCommissionConfig(): boolean {
    return this.auth.hasAnyPermission(['staff.manage', 'manage_users']);
  }

  canGeneratePayroll(): boolean {
    return this.auth.hasAnyPermission(['staff.manage', 'staff.request.approve', 'manage_users']);
  }

  canViewSlips(): boolean {
    return this.auth.hasAnyPermission(['staff.manage', 'staff.view', 'staff.request.approve', 'manage_users']);
  }

  canManageDeliveryPricing(): boolean {
    return this.auth.hasAnyPermission(['manage_delivery', 'manage_logistics']);
  }

  private canAccessStaffDirectory(): boolean {
    return this.auth.hasAnyPermission(['staff.manage', 'staff.view', 'manage_users']);
  }
}
